import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  useToaster,
  Pagination,
  IconButton,
  Modal,
  Message,
  Whisper,
  Tooltip
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import GearIcon from '@rsuite/icons/Gear';
import { MdFilterList, MdOutlineDeleteOutline, MdInfoOutline } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import NotificationDrawer from './NotificationDrawer';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminUsers } from '@/services/user.service';
import { fetchRemoteWorkingOperations } from '@/services/wfh.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { deleteRequest } from '@/services/request.service';
import ExceptionalDetailsDrawer from './ExceptionalDetailsDrawer';
import { IoMdNotificationsOutline } from 'react-icons/io';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const { operations, loading: operationsLoading, pagination } = useSelector(state => state.wfh);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const toaster = useToaster();
  const { t } = useTranslation();

  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState({ open: false, data: null });
  const [filters, setFilters] = useState({
    userId: '',
    status: '',
    validatorId: ''
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });

  // Filter users to exclude those with forgotten: true
  const filteredUsers = users.filter(user => user.forgotten !== true);

  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(
        fetchRemoteWorkingOperations(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
          page,
          limit,
          filters
        )
      );
    }
  }, [dateRange, page, limit, filters, dispatch]);

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'yellow';
      case 'accepted':
        return 'green';
      case 'Reject':
        return 'red';
      default:
        return 'grey';
    }
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const requestsToExport = operations.map(item => {
      const daysRequested = item.operations
        .map(operation => {
          const startDate = new Date(operation.date_debut).toLocaleDateString();
          const endDate = new Date(operation.date_fin).toLocaleDateString();
          return `${startDate} to ${endDate}`;
        })
        .join(', ');

      const validator = users.find(user => user._id === item.idReciever?._id);
      const validatorName = validator ? `${validator.firstname} ${validator.lastname}` : 'N/A';

      return {
        'Full Name': `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`,
        'Registration Number': item.idSender?.registrationNumber || '',
        'Days Requested': daysRequested || 'N/A',
        Status: item._id?.status || '',
        'Created At': new Date(item.createdAt).toLocaleString(),
        Validator: validatorName
      };
    });

    downloadExcel(requestsToExport, 'exceptional-wfh-requests');
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleDelete = rowData => {
    setSelectedRequest(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const requestId = selectedRequest._id || selectedRequest.id;
      await dispatch(deleteRequest(requestId));

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Request deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);

      if (dateRange && dateRange[0]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        endDate.setHours(23, 59, 59, 999);

        dispatch(
          fetchRemoteWorkingOperations(
            format(startDate, 'yyyy-MM-dd'),
            format(endDate, 'yyyy-MM-dd'),
            page,
            limit,
            filters
          )
        );
      }
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting request:')} {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);
    }
  };

  const handleDetails = rowData => {
    setShowDetailsDrawer({ open: true, data: rowData });
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      status: '',
      validatorId: ''
    });
    setPage(1);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <DateRangePicker
          appearance="default"
          placeholder={t('Select Date Range')}
          style={{ width: 230 }}
          value={dateRange}
          onChange={setDateRange}
          defaultValue={dateRange}
          editable={false}
          ranges={[
            {
              label: t('Last 30 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
            },
            {
              label: t('Last 7 Days'),
              value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
            },
            {
              label: t('Today'),
              value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
            }
          ]}
        />

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
          <Button appearance="primary" onClick={() => setShowNotificationDrawer(true)}>
            <IoMdNotificationsOutline style={{ marginRight: 8 }} /> {t('Notification Settings')}
          </Button>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 200, 400)}
        data={operations}
        loading={operationsLoading}
        rowHeight={80}
        wordWrap="break-word"
      >
        <Column flexGrow={4}>
          <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
          <Cell>
            {rowData => `${rowData.idSender?.firstname || ''} ${rowData.idSender?.lastname || ''}`}
          </Cell>
        </Column>

        <Column flexGrow={3}>
          <HeaderCell>{t('Registration Number')}</HeaderCell>
          <Cell>{rowData => rowData.idSender?.registrationNumber}</Cell>
        </Column>

        <Column flexGrow={4}>
          <HeaderCell>{t('Days Requested')}</HeaderCell>
          <Cell style={{ whiteSpace: 'pre-line' }}>
            {rowData => {
              const operation = rowData.operations?.[0];
              if (operation?.OperationType === 'REMOTE_WORKING') {
                const startDate = new Date(operation.date_debut);
                const endDate = new Date(operation.date_fin);

                const formattedStartDate = startDate.toLocaleDateString();
                const formattedEndDate = endDate.toLocaleDateString();

                if (formattedStartDate === formattedEndDate) {
                  return formattedStartDate;
                } else {
                  return `${formattedStartDate} to ${formattedEndDate}`;
                }
              }
              return '';
            }}
          </Cell>
        </Column>

        <Column flexGrow={3}>
          <HeaderCell>{t('Status')}</HeaderCell>
          <Cell>
            {rowData => (
              <Tag color={getStatusColor(rowData._id?.status)}>
                {t(rowData._id?.status || 'unknown')}
              </Tag>
            )}
          </Cell>
        </Column>

        <Column flexGrow={3}>
          <HeaderCell>{t('Validator')}</HeaderCell>
          <Cell>
            {rowData => {
              const receiverId = rowData.idReciever?._id;
              const validator = receiverId ? users.find(user => user._id === receiverId) : null;
              return validator ? `${validator.firstname} ${validator.lastname}` : 'N/A';
            }}
          </Cell>
        </Column>

        <Column flexGrow={3}>
          <HeaderCell>{t('Created At')}</HeaderCell>
          <Cell>{rowData => new Date(rowData.createdAt).toLocaleString()}</Cell>
        </Column>

        <Column width={100} flexGrow={0.5} fixed="right">
          <HeaderCell>{t('Actions')}</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (
              <Stack spacing={6}>
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip>{t('View Details')}</Tooltip>}
                >
                  <IconButton
                    size="xs"
                    icon={<Icon as={MdInfoOutline} />}
                    onClick={() => handleDetails(rowData)}
                    id={`details-${rowData._id}`}
                  />
                </Whisper>
                <IconButton
                  size="xs"
                  icon={<MdOutlineDeleteOutline color="red" />}
                  onClick={() => handleDelete(rowData)}
                  id={`delete-${rowData._id}`}
                />
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>

      <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={pagination?.total || operations.length}
          limitOptions={[10, 20, 30, 50]}
          limit={limit}
          activePage={page}
          onChangePage={setPage}
          onChangeLimit={handleChangeLimit}
        />
      </div>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Exceptional Home Office Request')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'Are you sure you want to delete this exceptional home office request? This action cannot be undone.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={filteredUsers}
        usersLoading={usersLoading}
        toaster={toaster}
      />

      <NotificationDrawer
        open={showNotificationDrawer}
        onClose={() => setShowNotificationDrawer(false)}
        users={filteredUsers}
        usersLoading={usersLoading}
        toaster={toaster}
      />

      <ExceptionalDetailsDrawer
        open={showDetailsDrawer.open}
        onClose={() => setShowDetailsDrawer({ open: false, data: null })}
        data={showDetailsDrawer.data}
      />
    </>
  );
};

export default DataTable;
